import { useState, useRef, useEffect } from 'react'
import { Plus, X } from 'lucide-react'

/**
 * One narrative fact rendered as a click-to-edit chip. Wraps naturally next
 * to its siblings inside a flex-wrap container (see ReviewSessionPage). Empty
 * edits delete the fact (consistent with how an empty Add chip auto-deletes
 * if the user opens-then-blurs without typing).
 *
 * The chip widens itself to accommodate longer fact text instead of forcing
 * single-line truncation — most facts are 1-2 short clauses, but multi-clause
 * recommendations need room. Hover reveals a small × delete affordance.
 */
export function FactPill({ value, lowConfidence = false, autoFocus = false, onCommit, onDelete }) {
  const [editing, setEditing] = useState(autoFocus && !value)
  const [draft, setDraft] = useState(value || '')
  const taRef = useRef(null)

  useEffect(() => {
    if (editing && taRef.current) {
      taRef.current.focus()
      taRef.current.select()
    }
  }, [editing])

  const commit = () => {
    setEditing(false)
    const next = draft.trim()
    if (next === '') {
      onDelete?.()
      return
    }
    if (next !== value) onCommit?.(next)
  }

  const cancel = () => {
    setEditing(false)
    setDraft(value || '')
    if (!value) onDelete?.() // newly-added empty pill — discard
  }

  if (editing) {
    return (
      <textarea
        ref={taRef}
        rows={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commit() }
          else if (e.key === 'Escape') { e.preventDefault(); cancel() }
        }}
        className="min-h-[28px] w-full resize-none rounded-2xl border border-terra-500 bg-white px-3 py-1 text-[13px] focus:outline-none sm:w-auto sm:min-w-[180px]"
      />
    )
  }

  return (
    <span
      className={`group inline-flex max-w-full items-start gap-1.5 rounded-2xl border bg-white px-3 py-1 text-[13px] leading-snug transition-colors hover:border-ink/30 ${lowConfidence ? 'border-fiscal-fg/35' : 'border-line'}`}
    >
      <button
        onClick={() => setEditing(true)}
        className="cursor-text whitespace-pre-wrap text-left"
      >
        {value}
      </button>
      {lowConfidence && (
        <span className="mt-0.5 shrink-0 rounded-full bg-fiscal-bg px-1.5 py-px text-[10px] font-medium text-fiscal-fg">
          à confirmer
        </span>
      )}
      <button
        onClick={onDelete}
        title="Supprimer ce fait"
        className="ml-0.5 shrink-0 rounded-full p-0.5 text-muted opacity-0 transition-opacity hover:bg-bg hover:text-terra-600 group-hover:opacity-100"
      >
        <X size={12} strokeWidth={2.2} />
      </button>
    </span>
  )
}

/** Dashed "+ Ajouter un fait" companion to FactPill. */
export function AddFactPill({ onAdd }) {
  return (
    <button
      onClick={onAdd}
      className="inline-flex items-center gap-1 rounded-2xl border border-dashed border-line bg-transparent px-3 py-1 text-[12.5px] text-muted transition-colors hover:border-ink/40 hover:text-ink"
    >
      <Plus size={12} strokeWidth={2.2} />
      Ajouter un fait
    </button>
  )
}
